import React from 'react';
import { Button, Spin, Table, Tag } from 'antd';
import { ReloadOutlined, SortAscendingOutlined, TeamOutlined, UserOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { AttendanceRow } from '../../services/p9410Api';
import { useP9410 } from './hooks/useP9410';
import './P9410Form.css';

/** BR-S02: row background color by status code */
const getRowClassName = (record: AttendanceRow): string => {
  const code = (record.status_code || '').trim();
  switch (code) {
    case 'S': return 'p9410-row-out';       // 外出 = light red
    case 'X': return 'p9410-row-enter6f';   // 入6F = light green
    case '':  return 'p9410-row-absent';     // 未到/輪休/請假 = light blue-grey
    default:  return '';                      // T=進入, Y=出6F = white
  }
};

/** BR-S01: status display tag color */
const statusTagColor = (display: string): string => {
  if (display === '進入') return 'green';
  if (display === '外出') return 'red';
  if (display === '入6F') return 'cyan';
  if (display === '出6F') return 'orange';
  if (display === '輪休') return 'blue';
  if (display.startsWith('請假')) return 'purple';
  if (display === '未到') return 'default';
  return 'default';
};

const P9410Form: React.FC = () => {
  const {
    loading, departments, attendanceData, selectedDept, sortBy,
    noData, currentTime, selectDepartment, toggleSort, refresh,
  } = useP9410();

  const columns: ColumnsType<AttendanceRow> = [
    { title: '員工代號', dataIndex: 'emp_code', key: 'emp_code', width: 90, align: 'center' },
    { title: '員工姓名', dataIndex: 'emp_name', key: 'emp_name', width: 100 },
    { title: '英文名稱', dataIndex: 'emp_name_en', key: 'emp_name_en', width: 120 },
    { title: '部門', dataIndex: 'dept_code', key: 'dept_code', width: 70, align: 'center' },
    { title: '分機', dataIndex: 'ext', key: 'ext', width: 60, align: 'center' },
    { title: '上班時間', dataIndex: 'clock_in', key: 'clock_in', width: 90, align: 'center' },
    { title: '最後打卡', dataIndex: 'last_punch', key: 'last_punch', width: 90, align: 'center' },
    {
      title: '出勤狀態', dataIndex: 'status_display', key: 'status_display', width: 100, align: 'center',
      render: (val: string) => <Tag color={statusTagColor(val)}>{val || '-'}</Tag>,
    },
    { title: '假別', dataIndex: 'leave_note', key: 'leave_note', width: 80 },
  ];

  return (
    <Spin spinning={loading}>
      <div className="p9410-container">
        {/* Header */}
        <div className="p9410-header">
          <h2 className="p9410-title">出勤現況查詢 (pw9410f1)</h2>
          <div className="p9410-clock">
            <span>{new Date().toLocaleDateString('zh-TW')}</span>
            <span>{currentTime}</span>
          </div>
        </div>

        {/* Department buttons (BR-I02) */}
        <div className="p9410-dept-panel">
          <div className="p9410-dept-buttons">
            <Button
              type={selectedDept === '' ? 'primary' : 'default'}
              icon={<TeamOutlined />}
              onClick={() => selectDepartment('')}
              size="small"
            >
              全公司
            </Button>
            {departments.map((dept) => (
              <Button
                key={dept.dept_code}
                type={selectedDept === dept.dept_code ? 'primary' : 'default'}
                onClick={() => selectDepartment(dept.dept_code)}
                size="small"
              >
                {dept.dept_name}
              </Button>
            ))}
          </div>
        </div>

        {/* Actions */}
        <div className="p9410-actions">
          <Button icon={<ReloadOutlined />} onClick={refresh} size="small">
            現況查詢
          </Button>
          <Button
            icon={sortBy === 'employee' ? <UserOutlined /> : <SortAscendingOutlined />}
            onClick={toggleSort}
            size="small"
          >
            {sortBy === 'employee' ? '按部門排序' : '按員工排序'}
          </Button>
          <span className="p9410-sort-label">
            *本資料約五分鐘自動更新
          </span>
        </div>

        {/* Update time */}
        {attendanceData && (
          <div className="p9410-update-time">
            最後更新: {attendanceData.query_time ? new Date(attendanceData.query_time).toLocaleTimeString('zh-TW', { hour12: false }) : '-'}
            ，共 {attendanceData.total} 人
          </div>
        )}

        {/* Attendance table */}
        {attendanceData && attendanceData.rows.length > 0 && (
          <Table<AttendanceRow>
            dataSource={attendanceData.rows}
            columns={columns}
            rowKey={(r) => r.emp_code}
            size="small"
            pagination={false}
            scroll={{ x: 800 }}
            bordered
            rowClassName={getRowClassName}
          />
        )}

        {noData && (
          <div className="p9410-no-data">查無符合條件的出勤資料!</div>
        )}
      </div>
    </Spin>
  );
};

export default P9410Form;
